export const MARKET_TZ = 'America/New_York';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdCompact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });

export const money = (n: number | null | undefined) => (n == null ? '—' : usd.format(n));
export const moneyShort = (n: number) => (Math.abs(n) >= 10_000 ? usdCompact.format(n) : usd.format(n).replace(/\.00$/, ''));
export const signedMoney = (n: number) => (n > 0 ? '+' : '') + usd.format(n);
export const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
export const num = (n: number, d = 0) => n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

export function price(n: number | null | undefined) {
  if (n == null) return '—';
  return n < 1 ? n.toFixed(4) : n.toFixed(2);
}

export const pnlClass = (n: number) => (n > 0 ? 'pos' : n < 0 ? 'neg' : '');

const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: MARKET_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
export const time = (ms: number) => timeFmt.format(new Date(ms));

export function dateLabel(date: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) {
  return new Date(date + 'T12:00:00Z').toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

export function duration(ms: number) {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Offset (seconds) of the market zone from UTC at an instant — used to show chart axes in exchange time. */
export function marketOffsetSec(utcSec: number) {
  const d = new Date(utcSec * 1000);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: MARKET_TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
      .formatToParts(d).filter((p) => p.type !== 'literal').map((p) => [p.type, Number(p.value)]),
  );
  return (Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - utcSec * 1000) / 1000;
}

export function todayMarket() {
  return marketDate(Date.now());
}

export function shiftDate(date: string, days: number) {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM-DD of an instant in New York time. */
export function marketDate(ms: number) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MARKET_TZ }).format(new Date(ms));
}

/** 4.7 KB, 12 MB, 1.0 GB */
export function bytes(n: number) {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(n >= 100 * 1024 ** 2 ? 0 : 1)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}
